/**
 * Turn a board extraction into a single-line diagram (via the generator). The result is a
 * Transaction the user previews and accepts; nothing is applied automatically.
 *
 * Which RCD feeds which breaker follows the usual Belgian layout (see `extractionToBoard`).
 * A photo shows neither the points nor the cables: circuits are created with no points and an
 * empty cable section, which the rule checker flags until they are filled in.
 */
import type { Id, Project, PropValue, Transaction } from '@elec/domain';
import { generateBoardOps, type BoardSpec, type CircuitSpec, type GroupSpec } from '@elec/editor';
import { earthNeeds } from '@elec/rules';
import type { BoardDevice, BoardExtraction } from './schema.ts';

/**
 * The device's values as element props. A value the photo did not show is set to '' (unknown)
 * rather than left out: left out, the symbol's default (16 A, curve C, 30 mA, type A) would
 * appear as if it had been read, and could hide a real non-conformity. R-DEV-01 flags them.
 */
const props = (d: BoardDevice): Record<string, PropValue> => {
  const p: Record<string, PropValue> = {};
  const rcd = d.deviceType === 'RCD' || d.deviceType === 'RCBO';
  const breaker = d.deviceType === 'MCB' || d.deviceType === 'RCBO';
  p.ratingA = d.ratingA ?? '';
  if (breaker) p.curve = d.curve ?? '';
  // A one-module breaker read as "1 pole" is a 1P+N in Belgian homes: keep the symbol's 2 poles.
  if (d.poles && !(breaker && d.poles < 2)) p.poles = d.poles;
  if (rcd) {
    p.sensitivityMa = d.sensitivityMa ?? '';
    p.rcdType = d.rcdType ?? '';
  }
  return p;
};

/**
 * Which RCD feeds which breaker is not visible on a photo; the import follows the usual layout:
 * - the main switch and a ≥ 100 mA RCD before the first breaker form the supply chain;
 * - a further RCD protects the breakers after it on the same rail; a rail without its own RCD
 *   is fed from the main RCD (a 30 mA group running on to the next rail must be moved by hand);
 * - an RCBO is its own RCD: it goes in the main group, never under another 30 mA RCD.
 */
export function extractionToBoard(ex: BoardExtraction): BoardSpec {
  const sorted = [...ex.devices].sort((a, b) => a.row - b.row || a.index - b.index);
  // An "RCD" with a circuit label and no breaker after it on its rail protects nothing: it feeds
  // the circuit itself, so it is an RCBO the model did not recognise (seen on Qwen 3.8).
  const devices = sorted.map((d, i) => {
    const next = sorted[i + 1];
    const feedsNothing = !next || next.row !== d.row || !['MCB', 'RCBO', 'fuse'].includes(next.deviceType);
    return d.deviceType === 'RCD' && d.label && feedsNothing ? { ...d, deviceType: 'RCBO' as const } : d;
  });
  const board: BoardSpec = { groups: [] };
  const mainGroup: GroupSpec = { circuits: [] };
  board.groups.push(mainGroup);
  let group = mainGroup;
  let row = devices[0]?.row;
  let seenCircuit = false;
  for (const d of devices) {
    if (d.row !== row) {
      row = d.row;
      group = mainGroup;
    }
    switch (d.deviceType) {
      case 'main-switch':
        if (!board.main) board.main = props(d);
        break;
      case 'RCD':
        if (!seenCircuit && !board.mainRcd && (d.sensitivityMa ?? 300) >= 100) board.mainRcd = props(d);
        else {
          group = { rcd: props(d), circuits: [] };
          board.groups.push(group);
        }
        break;
      case 'MCB':
      case 'RCBO':
      case 'fuse': {
        seenCircuit = true;
        const c: CircuitSpec = {
          label: d.label ?? '',
          device: {
            symbolId:
              d.deviceType === 'RCBO' ? 'iec.rcbo' : d.deviceType === 'fuse' ? 'iec.fuse' : 'iec.breaker',
            props: props(d),
          },
          points: [],
        };
        (d.deviceType === 'RCBO' ? mainGroup : group).circuits.push(c);
        break;
      }
      default:
        break; // meters, contactors, timers: not modelled by the import yet
    }
  }
  board.groups = board.groups.filter((g) => g.circuits.length || g.rcd);
  return board;
}

export function extractionToTransaction(
  project: Project,
  sheetId: Id,
  ex: BoardExtraction,
  label: string,
): Transaction {
  const board = extractionToBoard(ex);
  // The electrode is not on a board photo, but the diagram needs it: drawn, to be measured.
  if (earthNeeds(project).required) board.earth = {};
  return { label, ops: generateBoardOps(project, sheetId, board) };
}
